/* eslint-disable indent */
import * as DropdownMenu from '@radix-ui/react-dropdown-menu';
import { Box, useMediaQuery } from '@mui/system';
import { styled, useTheme } from '@mui/material/styles';
import { IconButton, Radio, Typography, SwipeableDrawer } from '@mui/material';

import PersonAddAltOutlinedIcon from '@mui/icons-material/PersonAddAltOutlined';
import { useEffect, useState } from 'react';
import { getActiveProfile, setActiveProfile } from '../context/AppContext';
import { UserType } from '../context/SignupContext';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAppSelector } from '../../store/store';
import ExpandMoreIcon from '../../icons/ExpandMoreIcon';
import ArtistMenuIcon from '../../assets/switchProfileIcons/ArtistMenuIcon';
import InfluencerMenuIcon from '../../assets/switchProfileIcons/InfluencerMenuIcon';
import CreatorMenuIcon from '../../assets/switchProfileIcons/CreatorMenuIcon';
import ModeEditIcon from '../../assets/switchProfileIcons/ModeEditIcon';
import { getProfileDisplayName } from '../../utils/profileDisplayNames';




const SwitchProfileMenu = () => {

  const [open, setOpen] = useState(false);
  const theme = useTheme();
  const menuProfiles = ['creator', 'influencer', 'artist'] as UserType[];
  const [activeProfile, setActiveProfileState] = useState<UserType | null>(getActiveProfile());
  const navigate = useNavigate();
  const location = useLocation();
  const isXsScreen = useMediaQuery(theme.breakpoints.down('sm'));
  const { profile, isLoading } = useAppSelector(e => e.profile);

  // Update active profile when location changes or on mount
  useEffect(() => {
    const currentProfile = getActiveProfile();
    setActiveProfileState(currentProfile);
  }, [location.pathname]);

  const StyledMenuContent = styled(DropdownMenu.Content)(({ theme }) => ({
    minWidth: '160px',
    backgroundColor: theme.palette.background.paper,
    color: theme.palette.text.primary,
    borderBottomLeftRadius: 8,
    borderBottomRightRadius: 8,
    padding: '8px 0',
  }));
  const StyledMenuItem = styled(DropdownMenu.Item)(({ theme }) => ({
    padding: '4px .2px 4px 4px',
    cursor: 'default',
    display: 'flex',
    justifyContent: 'space-between',
    gap: '8px',
    backgroundColor: theme.palette.background.paper,
    color: theme.palette.text.primary,
    borderRadius: theme.shape.borderRadius,
    '&:focus': {
      outline: 'none',
    },
  }));
  const menuItemTextStyle = {
    fontFamily: 'Inter',
    fontWeight: 600,
    fontSize: '12px',
    lineHeight: '100%',
    letterSpacing: '0px',
    verticalAlign: 'middle',
  };


  const [profileTypes, setProfileTypes] = useState({
    artist: {
      label: 'artist',
      icon: (props) => <ArtistMenuIcon {...props} />,
      profile: true,
    },
    creator: {
      label: 'creator',
      icon: (props) => <InfluencerMenuIcon {...props} />,
      profile: false,
    },
    influencer: {
      label: 'influencer',
      icon: (props) => <CreatorMenuIcon {...props} />,
      profile: false,
    },
  });

  const handleSwitch = ({ profileType }: { profileType: UserType }) => {
    setActiveProfile(profileType);
    switch (profileType) {
      case 'artist':
        navigate('/artist/dashboard', { replace: true });
        break;
      case 'creator':
        navigate('/creator/dashboard', { replace: true });
        break;
      case 'influencer':
        navigate('/influencer/dashboard', { replace: true });
        break;
      default:
        navigate('/');
    }
  };

  const handleEditOrUpdateProfile = ({ profileType, edit = false }: { profileType: UserType, edit?: boolean }) => {
    if (edit) setActiveProfile(profileType);
    
    // Special case: when editing artist profile, navigate to artist-profile page
    if (edit && profileType === 'artist') {
      navigate('/artist/artist-profile', { replace: true });
      return;
    }
    
    // Special case: when editing influencer profile, navigate to edit page
    if (edit && profileType === 'influencer') {
      navigate('/influencer/edit', { replace: true });
      return;
    }
    
    if (edit && profileType === 'creator') {
      navigate('/creator-profile', { replace: true });
      return;
    }
    
    switch (profileType) {
      case 'artist':
        // Artists have no setup steps - just navigate to account setup which will complete automatically
        navigate('/account/setup?userType=artist', { replace: true });
        break;
      case 'creator':
        // Creators have no setup steps - just navigate to account setup which will complete automatically
        navigate('/account/setup?userType=creator', { replace: true });
        break;
      case 'influencer':
        navigate('/account/setup?userType=influencer&setupStep=CHOOSE-CATEGORIES', { replace: true });
        break;
      default:
        navigate('/');
    }
  };

  useEffect(() => {
    if (!isLoading && profile) {
      setProfileTypes(prev => ({
        ...prev,
        artist: {
          ...prev.artist,
          profile: profile.artist != null && profile.artist.accountType != null,
        },
        creator: {
          ...prev.creator,
          profile: profile.creator != null && profile.creator.accountType != null,
        },
        influencer: {
          ...prev.influencer,
          profile: profile.influencer != null && profile.influencer.accountType != null,
        },
      }));
    }
  }, [isLoading, profile]);

  return (
    <DropdownMenu.Root open={open} onOpenChange={setOpen} modal={false} >
      {isXsScreen ?
        <>
          <Box sx={{ height: '100%', position: 'relative' }}>
            {/* Mobile Trigger */}
            <Box
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                setOpen(!open);
              }}
              sx={{
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: '2px',
                height: '100%',
                px: 0,
                minWidth: activeProfile?.toLocaleLowerCase() != 'influencer' ? '134px' : '149px',
                backgroundColor: open ? '#1D1B25' : '#1D1B25',
                borderRadius: '40px',
                paddingRight: "-20px",
              }}>

              <Box sx={{ backgroundColor: '#18181B', borderRadius: '50%', width: '24px', height: '24px', display: 'flex', alignItems: 'center', justifyContent: 'center', }}>
                {profileTypes[activeProfile]?.icon({ width: 15, height: 14 })}
              </Box>
              <Typography
                sx={{
                  fontFamily: 'Inter',
                  fontWeight: 400,
                  fontSize: '16px',
                  lineHeight: '100%',
                  letterSpacing: '0px',
                  verticalAlign: 'middle',
                  display: 'inline-flex',
                  alignItems: 'center'
                }}
              >{activeProfile ? getProfileDisplayName(activeProfile) : ''}</Typography>

              <IconButton
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  setOpen(!open);
                }}
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                }}
              >
                <ExpandMoreIcon
                  style={{
                    width: 24,
                    height: 24,
                    transform: open ? 'rotate(180deg)' : 'rotate(0deg)',
                    transition: 'transform 0.2s ease',
                  }}
                />
              </IconButton>
            </Box>

            <SwipeableDrawer
              anchor="bottom"
              open={open}
              onClose={() => setOpen(false)}
              onOpen={() => setOpen(true)}
              swipeAreaWidth={0}
              disableSwipeToOpen={true}
              ModalProps={{
                keepMounted: false,
              }}
              PaperProps={{
                sx: {
                  borderTopLeftRadius: 16,
                  borderTopRightRadius: 16,
                  backgroundColor: '#1D1B25',
                  color: 'white',
                  maxHeight: '50vh',
                }
              }}
            >
              <Box
                sx={{
                  padding: '20px 16px',
                  minHeight: '200px',
                }}
              >
                <Box sx={{
                  display: 'flex', justifyContent: 'center', alignItems: 'flex-start',
                  left: "50%", top: window.innerHeight - 345, position: 'fixed',
                  transform: 'translateX(-50%)',
                }}>
                  <IconButton
                    onClick={() => setOpen(false)}
                    sx={{
                      color: 'white',
                      fontSize: '24px',
                      width: 54,
                      height: 54,
                      borderRadius: '50%',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      backgroundColor: '#1D1B25',
                      outline:"none",
                      border:"none",
                      '&:hover': {
                        backgroundColor: 'rgba(255,255,255,0.1)',
                        borderColor: 'white'
                      }
                    }}
                  >

                    <svg xmlns="http://www.w3.org/2000/svg" width="17.156px" height="17.156px" viewBox="0 0 18 18" fill="none">
                      <path d="M8.57812 11.2031L14.0156 16.6406C14.3594 16.9844 14.7969 17.1562 15.3281 17.1562C15.8594 17.1562 16.2969 16.9844 16.6406 16.6406C16.9844 16.2969 17.1562 15.8594 17.1562 15.3281C17.1562 14.7969 16.9844 14.3594 16.6406 14.0156L11.2031 8.57812L16.6406 3.14062C16.9844 2.79688 17.1562 2.35938 17.1562 1.82812C17.1562 1.29688 16.9844 0.859376 16.6406 0.515626C16.2969 0.171876 15.8594 0 15.3281 0C14.7969 0 14.3594 0.171876 14.0156 0.515626L8.57812 5.95312L3.14062 0.515626C2.79688 0.171876 2.35938 0 1.82812 0C1.29688 0 0.859376 0.171876 0.515626 0.515626C0.171876 0.859376 0 1.29688 0 1.82812C0 2.35938 0.171876 2.79688 0.515626 3.14062L5.95312 8.57812L0.515626 14.0156C0.171876 14.3594 0 14.7969 0 15.3281C0 15.8594 0.171876 16.2969 0.515626 16.6406C0.859376 16.9844 1.29688 17.1562 1.82812 17.1562C2.35938 17.1562 2.79688 16.9844 3.14062 16.6406L8.57812 11.2031Z" fill="white" />
                    </svg>


                  </IconButton>
                </Box>
                {/* Header */}
                <Box sx={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  marginBottom: 3
                }}>
                  <Typography
                    sx={{
                      fontFamily: 'Inter',
                      fontWeight: 600,
                      fontSize: '12px',
                      lineHeight: '100%',
                      letterSpacing: '0px',
                      color: 'white'
                    }}
                  >
                    Switch Account
                  </Typography>

                </Box>

                {/* Profile List */}
                {menuProfiles?.map((item) => {
                  const isProfile = profileTypes[item]?.profile;
                  const isActive = activeProfile?.toLocaleLowerCase() === profileTypes[item]?.label;

                  return (
                    <Box
                      key={profileTypes[item]?.label}
                      sx={{
                        background: 'transparent',
                        marginY: 1,
                        width: '100%',
                        padding: '12px',
                        borderRadius: 4,
                        borderBottom: menuProfiles.indexOf(item) !== menuProfiles.length - 1 ? '1px solid rgba(255,255,255,0.1)' : 'none',
                        display: 'flex',
                        alignItems: 'center',
                        backgroundColor: "#000000",
                        cursor: isProfile ? 'pointer' : 'default',
                      }}
                    >
                      <Box
                        sx={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: 2,
                          flex: 1
                        }}
                      >
                        <Box
                          sx={{
                            background: '#1D1B25',
                            padding: 1.5,
                            borderRadius: 2,
                            width: 48,
                            height: 48,
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center'
                          }}
                        >
                          {profileTypes[item]?.icon({
                            width: 24,
                            height: 24,
                            color: isActive ? 'white' : '#A8A8A8'
                          })}
                        </Box>

                        <Box sx={{ flex: 1 }}>
                          <Typography
                            sx={{
                              fontFamily: 'Inter',
                              fontWeight: 600,
                              fontSize: '16px',
                              lineHeight: '100%',
                              color: isActive ? 'white' : '#A8A8A8',
                              marginBottom: isProfile ? 0.5 : 0
                            }}
                            >
                            {getProfileDisplayName(item)}
                          </Typography>
                          {isProfile && (
                            <Typography
                              sx={{
                                fontFamily: 'Inter',
                                fontWeight: 400,
                                fontSize: '12px',
                                color: '#666',
                              }}
                            >

                            </Typography>
                          )}
                        </Box>
                      </Box>

                      <Box
                        sx={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: 1
                        }}
                      >
                        {isProfile ? (
                          <IconButton
                            sx={{
                              color: '#A8A8A8',  
                              padding: 1,
                              '&:hover': {
                                backgroundColor: 'rgba(255,255,255,0.1)',
                                color: 'white'
                              }
                            }}
                            onClick={(e) => {
                              e.stopPropagation();
                              setOpen(false);
                              handleEditOrUpdateProfile({
                                profileType: profileTypes[item]?.label as UserType,
                                edit: true
                              });
                            }}
                          >
                            <ModeEditIcon sx={{ width: 24, height: 24 }} />
                          </IconButton>
                        ) : (
                          <IconButton
                            sx={{
                              color: 'white',
                              width:"24px",
                              height:"24px",
                              padding: 1,
                              '&:hover': {
                                backgroundColor: 'rgba(255,255,255,0.1)',
                                color: 'white'
                              }
                            }}
                            onClick={(e) => {
                              e.stopPropagation();
                              setOpen(false);
                              handleEditOrUpdateProfile({
                                profileType: profileTypes[item]?.label as UserType
                              });
                            }}
                          >
                            <PersonAddAltOutlinedIcon sx={{ width: 24, height: 24 }} />
                          </IconButton>
                        )}

                        <Radio
                          sx={{
                            color: '#A8A8A8',
                            '&.Mui-checked': {
                              color: 'white',
                            },
                            opacity: isProfile ? 1 : 0.5,
                            padding: 1
                          }}
                          checked={isActive}
                          onClick={() => {
                            if (isProfile) {
                              handleSwitch({ profileType: profileTypes[item]?.label as UserType });
                              setOpen(false);
                            }
                          }}
                          // onClick={(e) => e.stopPropagation()}
                        />
                      </Box>
                    </Box>
                  );
                })}
              </Box>
            </SwipeableDrawer>
          </Box>
        </> :
        <>
          <Box sx={{ height: '100%', position: 'relative' }}>
            <DropdownMenu.Trigger asChild>
              <Box
                sx={{
                  cursor: 'pointer',
                  display: { xs: 'none', md: 'flex' },
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: '8px',
                  height: '100%',
                  px: 2,
                  minWidth: activeProfile?.toLocaleLowerCase() != 'influencer' ? '134px' : '149px',
                  backgroundColor: open ? '#1D1B25' : '',
                }}>
                <Box sx={{ backgroundColor: '#18181B', borderRadius: '50%', width: '24px', height: '24px', display: 'flex', alignItems: 'center', justifyContent: 'center', }}>
                  {profileTypes[activeProfile]?.icon({ width: 15, height: 14 })}
                </Box>
                <Typography
                  sx={{
                    fontFamily: 'Inter',
                    fontWeight: 400,
                    fontSize: '16px',
                    lineHeight: '100%',
                    letterSpacing: '0px',
                    verticalAlign: 'middle',
                    display: 'inline-flex',
                    alignItems: 'center'
                  }}
                >{activeProfile ? getProfileDisplayName(activeProfile) : ''}</Typography>
                <IconButton
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                  }}
                >
                  <ExpandMoreIcon
                    style={{
                      width: 24,
                      height: 24,
                      transform: open ? 'rotate(180deg)' : 'rotate(0deg)',
                      transition: 'transform 0.2s ease',
                    }}
                  />
                </IconButton>
              </Box>
            </DropdownMenu.Trigger>
            <StyledMenuContent
              align="end"
              side="bottom"
              onCloseAutoFocus={(e) => e.preventDefault()}
              sx={{
                minWidth: '220px', padding: '12px', zIndex: 1000,
                position: 'absolute',
                transformOrigin: 'top right',
                right: 0
              }}>
              <Typography
                sx={{
                  fontFamily: 'Inter',
                  fontWeight: 600,
                  fontSize: '12px',
                  lineHeight: '100%',
                  letterSpacing: '0px',
                  verticalAlign: 'middle',
                  marginBottom: 2,
                  marginTop: 1
                }}
              >
                Switch Profile
              </Typography>
              {menuProfiles?.map((item) => {
                const isProfile = profileTypes[item]?.profile;
                return (<StyledMenuItem key={profileTypes[item]?.label} sx={{ background: 'black', marginY: 1, width: '312.75px' }}>
                  <Box
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 1
                    }}
                  >
                    <Box
                      sx={{
                        background: '#1D1B25',
                        padding: 1,
                        borderRadius: 1,
                        width: 50,
                        height: 50,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center'
                      }}
                    >
                      {profileTypes[item]?.icon({ width: 28, height: 28 })}
                    </Box>
                    <Typography sx={{ ...menuItemTextStyle, color: activeProfile?.toLocaleLowerCase() == profileTypes[item]?.label ? 'white' : '#A8A8A8' }}>{getProfileDisplayName(item)}</Typography>
                  </Box>
                  <Box
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center'
                    }}>
                    {isProfile ?
                      (
                        <ModeEditIcon sx={{ cursor: 'pointer' }} onClick={() => handleEditOrUpdateProfile({ profileType: profileTypes[item]?.label as UserType, edit: true })} />
                      ) : (
                        <PersonAddAltOutlinedIcon sx={{ cursor: 'pointer' }} onClick={() => handleEditOrUpdateProfile({ profileType: profileTypes[item]?.label as UserType })} />
                      )
                    }
                    <Radio
                      sx={{
                        color: 'white',
                        '&.Mui-checked': {
                          color: 'white',
                        },
                        opacity: isProfile ? 1 : .5
                      }}
                      onClick={() => isProfile ? handleSwitch({ profileType: profileTypes[item]?.label as UserType }) : null} checked={activeProfile?.toLocaleLowerCase() == profileTypes[item]?.label} />
                  </Box>
                </StyledMenuItem>);
              })}
            </StyledMenuContent>
          </Box>

        </>}

    </DropdownMenu.Root >
  );
};

export default SwitchProfileMenu;
